'use client';

import React from 'react';
import { SENDVC_PROFILES } from '@/lib/profiles';

/**
 * The three social profiles SendVC actually owns, as inline SVG.
 *
 * WHY INLINE SVG AND NOT AN ICON PACKAGE: three icons do not justify a
 * dependency, a font, or three network requests. These ship inside the
 * already-downloaded HTML/JS and cost nothing extra at runtime.
 *
 * WHY THESE THREE AND NO OTHERS: every URL here has been loaded and confirmed
 * to be SendVC's. That is the same rule `lib/seo-config.ts` enforces for
 * `sameAs`, and it exists because a link to a profile that does not exist —
 * or worse, to a stranger's — actively breaks entity resolution instead of
 * helping it. Two concrete near-misses this list is built to avoid:
 *
 *   - The X handle is `@SendVCapp`. Bare `@sendvc` belongs to an unrelated
 *     third party. Linking it would hand SendVC's entity signal to a stranger.
 *   - The YouTube link points at the CHANNEL, not at a video. The video id
 *     `3d2d4XJYqg0` was supplied twice as "the YouTube page" and is dead —
 *     the uploader removed it. YouTube serves HTTP 200 for removed videos, so
 *     a status-code link check calls it healthy; only loading the page shows
 *     "Dieses Video wurde vom Uploader entfernt". Verify YouTube links by
 *     rendering them, never by status code.
 *
 * `rel="me"` is deliberate: it is the microformats convention for "this is
 * another profile of the same entity", and it corroborates the `sameAs` array
 * in the Organization JSON-LD for crawlers that read links rather than
 * structured data.
 */

/**
 * Resolves a URL from lib/profiles.ts by label, so these icons cannot drift
 * out of sync with the `sameAs` array. Throws at module load if a label is
 * missing — a build-time failure is far better than a footer icon silently
 * pointing at the wrong company.
 */
const profileHref = (label: string): string => {
  const hit = SENDVC_PROFILES.find((p) => p.label === label);
  if (!hit) throw new Error(`SocialLinks: no profile named "${label}" in lib/profiles.ts`);
  return hit.href;
};

interface SocialLink {
  href: string;
  label: string;
  icon: React.ReactNode;
}

const SOCIAL_LINKS: SocialLink[] = [
  {
    href: profileHref('X'),
    label: 'SendVC on X',
    icon: (
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    ),
  },
  {
    href: profileHref('YouTube'),
    label: 'SendVC on YouTube',
    icon: (
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z" />
    ),
  },
  {
    href: profileHref('LinkedIn'),
    label: 'SendVC on LinkedIn',
    icon: (
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065Zm1.782 13.019H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z" />
    ),
  },
];

const SocialLinks: React.FC<{ className?: string }> = ({ className = '' }) => (
  <ul className={`flex items-center gap-4 ${className}`}>
    {SOCIAL_LINKS.map(({ href, label, icon }) => (
      <li key={href}>
        <a
          href={href}
          target="_blank"
          rel="me noopener noreferrer"
          aria-label={label}
          title={label}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-800 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400"
        >
          {/* aria-hidden + the label on the anchor: the icon is decorative,
              the accessible name lives on the link itself. */}
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-5 w-5"
            aria-hidden="true"
            focusable="false"
          >
            {icon}
          </svg>
        </a>
      </li>
    ))}
  </ul>
);

export { SocialLinks, SOCIAL_LINKS };
